import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Input,
  Badge,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { Catalogue, CustomerInquiry } from "../../../types/catalogue.types";
import notifications from "../../../common/Components/Notifications/Notification";
import {
  loadCatalogueCommerceRollup,
  type ProductCommerceSignal,
} from "../catalogueShopperSignals";

interface InquiriesTabProps {
  catalogue: Catalogue;
  focusInquiryId?: string;
  focusSubmissionId?: string;
}

type StudioSubmissionRow = {
  key: string;
  kind: "cart" | "wishlist";
  id: string;
  clientName: string;
  clientContact: string;
  clientEmail: string;
  clientMessage: string;
  source?: string;
  catalogueVisitorId?: string;
  submittedAt: Date | null;
  cartItems: any[];
  wishlistItems: any[];
  totalItems: number;
  totalUnits: number;
  summaryLine: string;
};

function submissionMongoId(doc: any): string {
  const raw = doc?.id ?? doc?._id;
  if (raw && typeof raw === "object" && "$oid" in raw) {
    return String((raw as { $oid: string }).$oid);
  }
  return String(raw ?? "");
}

function coerceSubmissionDate(v: unknown): Date | null {
  if (v == null) return null;
  if (v instanceof Date) return v;
  if (typeof v === "number") return new Date(v);
  if (typeof v === "string") {
    const t = Date.parse(v);
    return Number.isNaN(t) ? null : new Date(t);
  }
  if (typeof v === "object" && v !== null && "$date" in v) {
    return coerceSubmissionDate((v as { $date: unknown }).$date);
  }
  return null;
}

function formatMrpSnapshot(raw?: string): string {
  if (raw == null || raw === "") return "—";
  const n = parseFloat(String(raw).replace(/[^\d.]/g, ""));
  if (!Number.isFinite(n)) return String(raw);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Math.round(n));
}

function buildStudioSubmissionRows(wishlists: any[], carts: any[]): StudioSubmissionRow[] {
  const out: StudioSubmissionRow[] = [];

  (wishlists || []).forEach((w, i) => {
    const items = Array.isArray(w?.wishlistItems) ? w.wishlistItems : [];
    const mongoId = submissionMongoId(w);
    const id = mongoId || `wishlist-row-${i}`;
    /** Include index so expand state never collides when ids are missing or duplicated in API payloads. */
    const stableKey = `wishlist-${i}-${mongoId || "na"}`;
    const submittedAt = coerceSubmissionDate(w?.submissionDate ?? w?.lastUpdated);
    const n = items.length;
    const clientName = String(w?.clientName || "—").trim() || "—";
    const clientContact = String(w?.clientContact || "").trim();
    out.push({
      key: stableKey,
      kind: "wishlist",
      id,
      clientName,
      clientContact,
      clientEmail: String(w?.clientEmail || ""),
      clientMessage: String(w?.clientMessage || ""),
      source: w?.source ? String(w.source) : undefined,
      catalogueVisitorId: w?.catalogueVisitorId ? String(w.catalogueVisitorId) : undefined,
      submittedAt,
      cartItems: [],
      wishlistItems: items,
      totalItems: n,
      totalUnits: n,
      summaryLine:
        n === 0 ? "Shortlist (empty)" : `${n} shortlist item${n === 1 ? "" : "s"}`,
    });
  });

  (carts || []).forEach((c, i) => {
    const items = Array.isArray(c?.cartItems) ? c.cartItems : [];
    const mongoId = submissionMongoId(c);
    const id = mongoId || `cart-row-${i}`;
    const stableKey = `cart-${i}-${mongoId || "na"}`;
    const submittedAt = coerceSubmissionDate(c?.submissionDate ?? c?.lastUpdated);
    const n = items.length;
    let units = 0;
    for (const line of items) {
      units += Math.max(1, Math.trunc(Number(line?.quantity) || 1));
    }
    const clientName = String(c?.clientName || "—").trim() || "—";
    const clientContact = String(c?.clientContact || "").trim();
    out.push({
      key: stableKey,
      kind: "cart",
      id,
      clientName,
      clientContact,
      clientEmail: String(c?.clientEmail || ""),
      clientMessage: String(c?.clientMessage || ""),
      source: c?.source ? String(c.source) : undefined,
      catalogueVisitorId: c?.catalogueVisitorId ? String(c.catalogueVisitorId) : undefined,
      submittedAt,
      cartItems: items,
      wishlistItems: [],
      totalItems: n,
      totalUnits: units,
      summaryLine:
        n === 0 ? "Cart (empty)" : `${n} line${n === 1 ? "" : "s"} · ${units} pc${units === 1 ? "" : "s"}`,
    });
  });

  out.sort((a, b) => {
    const ta = a.submittedAt?.getTime() ?? 0;
    const tb = b.submittedAt?.getTime() ?? 0;
    return tb - ta;
  });

  return out;
}

const InquiriesTab: React.FC<InquiriesTabProps> = ({
  catalogue,
  focusInquiryId,
  focusSubmissionId,
}) => {
  const [inquiries, setInquiries] = useState<CustomerInquiry[]>([]);
  const [selectedInquiry, setSelectedInquiry] = useState<CustomerInquiry | null>(null);
  const [replyMessage, setReplyMessage] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterPriority, setFilterPriority] = useState<string>("all");
  const [loading, setLoading] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [commerceByProduct, setCommerceByProduct] = useState<
    Record<string, ProductCommerceSignal>
  >({});
  const [commerceLoading, setCommerceLoading] = useState(false);
  const [commerceWishlists, setCommerceWishlists] = useState<any[]>([]);
  const [commerceCarts, setCommerceCarts] = useState<any[]>([]);
  const [expandedSubmissionKeys, setExpandedSubmissionKeys] = useState<Record<string, boolean>>(
    {}
  );
  const [submissionDetail, setSubmissionDetail] = useState<StudioSubmissionRow | null>(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token || ""}`,
    };
  };

  const toUiStatus = (status: string): "new" | "replied" | "closed" => {
    const normalized = (status || "").toUpperCase();
    if (normalized === "RESPONDED") return "replied";
    if (normalized === "CLOSED") return "closed";
    return "new";
  };

  const normalizeInquiry = (item: any): CustomerInquiry => {
    const id = String(item?.id || item?._id || "");
    const productName =
      item?.productItems?.[0]?.productName ||
      (item?.productIds?.length ? `Product (${item.productIds.length})` : "General Inquiry");

    return {
      id,
      customerId: id,
      customerName: item?.clientName || "Unknown client",
      productId: item?.productItems?.[0]?.productId || item?.productIds?.[0],
      productName,
      message: item?.clientMessage || "",
      priority: "medium",
      status: toUiStatus(item?.status),
      timestamp: item?.inquiryDate
        ? new Date(item.inquiryDate).toLocaleString("en-IN")
        : "N/A",
      phone: item?.clientContact || "",
      email: item?.clientEmail || "",
    };
  };

  const loadInquiries = async () => {
    if (!catalogue?.id) return;
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_SERVERURL}/catalogue-inquiry/catalogue/${catalogue.id}`,
        { headers: getAuthHeaders() }
      );
      const result = await response.json();

      if (response.ok && result?.success) {
        const normalized = (result?.data || []).map(normalizeInquiry);
        setInquiries(normalized);
        setSelectedInquiry((prev) => {
          if (focusInquiryId) {
            return normalized.find((inq: CustomerInquiry) => inq.id === focusInquiryId) || prev || null;
          }
          return prev
            ? normalized.find((inq: CustomerInquiry) => inq.id === prev.id) || normalized[0] || null
            : normalized[0] || null;
        });
      } else {
        notifications({
          title: "Error",
          message: result?.message || "Failed to load inquiries",
          type: "danger",
        });
      }
    } catch (error) {
      notifications({
        title: "Error",
        message: "Unable to load inquiries right now",
        type: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadCommerce = async () => {
    if (!catalogue?.id) return;
    setCommerceLoading(true);
    try {
      const { byProductId, wishlists, carts } = await loadCatalogueCommerceRollup(catalogue.id);
      setCommerceByProduct(byProductId);
      setCommerceWishlists(Array.isArray(wishlists) ? wishlists : []);
      setCommerceCarts(Array.isArray(carts) ? carts : []);
    } catch {
      setCommerceByProduct({});
      setCommerceWishlists([]);
      setCommerceCarts([]);
    } finally {
      setCommerceLoading(false);
    }
  };

  useEffect(() => {
    loadInquiries();
    loadCommerce();
  }, [catalogue?.id]);

  const draftInterestRows = useMemo(() => {
    const entries = Object.entries(commerceByProduct).filter(
      ([, v]) => v.draftShortlistVisitors > 0 || v.draftCartVisitors > 0
    );
    entries.sort((a, b) => {
      const da =
        a[1].draftShortlistVisitors +
        a[1].draftCartVisitors +
        a[1].draftCartUnits * 0.01;
      const db =
        b[1].draftShortlistVisitors +
        b[1].draftCartVisitors +
        b[1].draftCartUnits * 0.01;
      return db - da;
    });
    return entries.map(([productKey, v]) => ({ productKey, ...v }));
  }, [commerceByProduct]);

  const submittedInterestRows = useMemo(() => {
    const entries = Object.entries(commerceByProduct).filter(
      ([, v]) => v.shortlistSubmissions > 0 || v.cartSubmissions > 0
    );
    entries.sort((a, b) => {
      const sa = a[1].shortlistSubmissions + a[1].cartSubmissions;
      const sb = b[1].shortlistSubmissions + b[1].cartSubmissions;
      return sb - sa;
    });
    return entries.map(([productKey, v]) => ({ productKey, ...v }));
  }, [commerceByProduct]);

  const submittedClientRows = useMemo(
    () => buildStudioSubmissionRows(commerceWishlists, commerceCarts),
    [commerceWishlists, commerceCarts]
  );

  useEffect(() => {
    if (!focusInquiryId) return;
    const hit = inquiries.find((inq) => inq.id === focusInquiryId);
    if (hit) setSelectedInquiry(hit);
  }, [focusInquiryId, inquiries]);

  useEffect(() => {
    if (!focusSubmissionId) return;
    const row = submittedClientRows.find((r) => r.id === focusSubmissionId);
    if (row) {
      setExpandedSubmissionKeys((prev) => ({ ...prev, [row.key]: true }));
      setSubmissionDetail(row);
    }
  }, [focusSubmissionId, submittedClientRows]);

  const toggleSubmissionExpanded = (key: string) => {
    setExpandedSubmissionKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Filter inquiries
  const filteredInquiries = useMemo(() => {
    return inquiries.filter(inquiry => {
      const matchesStatus = filterStatus === "all" || inquiry.status === filterStatus;
      const matchesPriority = filterPriority === "all" || inquiry.priority === filterPriority;
      return matchesStatus && matchesPriority;
    });
  }, [inquiries, filterStatus, filterPriority]);

  // Get priority badge
  const getPriorityBadge = (priority: string) => {
    const badgeProps = {
      high: { color: "danger", text: "HIGH" },
      medium: { color: "warning", text: "MED" },
      low: { color: "secondary", text: "LOW" }
    };
    const props = badgeProps[priority as keyof typeof badgeProps];
    return <Badge color={props.color}>{props.text}</Badge>;
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    const badgeProps = {
      new: { color: "primary", text: "NEW" },
      replied: { color: "success", text: "REPLIED" },
      closed: { color: "secondary", text: "CLOSED" }
    };
    const props = badgeProps[status as keyof typeof badgeProps];
    return <Badge color={props.color}>{props.text}</Badge>;
  };

  // Create WhatsApp link
  const createWhatsAppLink = (inquiry: CustomerInquiry) => {
    if (!inquiry.phone) return "";
    const cleanPhone = inquiry.phone.replace(/\D/g, "");
    const message = encodeURIComponent(
      `Hi ${inquiry.customerName}, regarding ${inquiry.productName || "your inquiry"}: ${inquiry.message}\n\n— HKP Team`
    );
    return `https://wa.me/${cleanPhone}?text=${message}`;
  };

  // Create email link
  const createEmailLink = (inquiry: CustomerInquiry) => {
    const subject = encodeURIComponent(`Re: ${inquiry.productName || "Your Inquiry"}`);
    const body = encodeURIComponent(
      `Hi ${inquiry.customerName},\n\nRegarding ${inquiry.productName || "your inquiry"}: ${inquiry.message}\n\n${replyMessage || "[Your reply here]"}\n\nBest regards,\nHKP Team`
    );
    const mailto = inquiry.email ? `mailto:${inquiry.email}?` : `mailto:?`;
    return `${mailto}subject=${subject}&body=${body}`;
  };

  const handleSendReply = async () => {
    if (!selectedInquiry) return;
    if (!replyMessage.trim()) {
      notifications({
        title: "Validation",
        message: "Please enter a reply message",
        type: "warning",
      });
      return;
    }

    try {
      setSending(true);
      const response = await fetch(
        `${process.env.REACT_APP_SERVERURL}/catalogue-inquiry/respond`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            inquiryId: selectedInquiry.id,
            artisanResponse: replyMessage.trim(),
            responseMethod: "INTERNAL",
            notes: "Responded from catalogue manager",
          }),
        }
      );
      const result = await response.json();
      if (response.ok && result?.success) {
        notifications({
          title: "Success",
          message: "Reply sent successfully",
          type: "success",
        });
        setReplyMessage("");
        await loadInquiries();
      } else {
        notifications({
          title: "Error",
          message: result?.message || "Failed to send reply",
          type: "danger",
        });
      }
    } catch {
      notifications({
        title: "Error",
        message: "Unable to send reply right now",
        type: "danger",
      });
    } finally {
      setSending(false);
    }
  };

  const handleMarkAsClosed = async () => {
    if (!selectedInquiry) return;
    try {
      setSending(true);
      const response = await fetch(
        `${process.env.REACT_APP_SERVERURL}/catalogue-inquiry/${selectedInquiry.id}/status?status=CLOSED`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
        }
      );
      const result = await response.json();
      if (response.ok && result?.success) {
        notifications({
          title: "Success",
          message: "Inquiry marked as closed",
          type: "success",
        });
        await loadInquiries();
      } else {
        notifications({
          title: "Error",
          message: result?.message || "Failed to update status",
          type: "danger",
        });
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <h3 className="catalogue-tab-heading">Customer Inquiries</h3>

      <Card className="mb-4 border-0 shadow-sm">
        <CardHeader
          style={{
            backgroundColor: "#f7f5f0",
            borderBottom: "1px solid #ebe6dc",
          }}
        >
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h6 className="catalogue-panel-title">Shopper activity (this catalogue)</h6>
              <small className="text-muted">
                <strong>Live draft</strong> = what visitors are building on their shelf (may change).
                <strong className="ms-1">Submitted</strong> = what they sent from &quot;My selections&quot;
                (treat as final for follow-up). Those submissions also appear on the Activity timeline.
              </small>
            </div>
            <Button
              size="sm"
              outline
              style={{ borderColor: "#0071E3", color: "#0071E3" }}
              onClick={() => loadCommerce()}
              disabled={commerceLoading}
            >
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {commerceLoading &&
          draftInterestRows.length === 0 &&
          submittedInterestRows.length === 0 &&
          submittedClientRows.length === 0 ? (
            <div className="p-3 text-muted small">Loading shopper activity…</div>
          ) : null}

          <div
            className="px-3 py-2 border-bottom"
            style={{ backgroundColor: "#eff6ff" }}
          >
            <div className="fw-bold small text-primary mb-0">Live draft (shelf only)</div>
            <div className="text-muted small">
              Visitors exploring the shared link — not yet submitted to you.
            </div>
          </div>
          {draftInterestRows.length === 0 ? (
            <div className="p-3 text-muted small border-bottom">
              No live draft activity for this catalogue right now.
            </div>
          ) : (
            <div className="table-responsive border-bottom">
              <Table hover className="mb-0 align-middle">
                <thead style={{ backgroundColor: "#f8fafc" }}>
                  <tr className="small text-muted">
                    <th>Product</th>
                    <th>SKU / ID</th>
                    <th>Shortlist (visitors)</th>
                    <th>Cart (visitors · pcs)</th>
                    <th>Last shelf update</th>
                  </tr>
                </thead>
                <tbody>
                  {draftInterestRows.map((row) => (
                    <tr key={`draft-${row.productKey}`}>
                      <td className="fw-semibold">{row.productName || "—"}</td>
                      <td>
                        <code className="small">{row.productKey}</code>
                      </td>
                      <td>
                        {row.draftShortlistVisitors > 0 ? (
                          <Badge pill color="primary" className="fw-semibold">
                            {row.draftShortlistVisitors} visitor
                            {row.draftShortlistVisitors === 1 ? "" : "s"}
                          </Badge>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        {row.draftCartVisitors > 0 ? (
                          <Badge pill color="info" className="fw-semibold">
                            {row.draftCartVisitors}v · {row.draftCartUnits} pcs
                          </Badge>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="small text-muted">
                        {row.draftLastActivityIso
                          ? new Date(row.draftLastActivityIso).toLocaleString("en-IN")
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          <div
            className="px-3 py-2 border-bottom"
            style={{ backgroundColor: "#f0fdf4" }}
          >
            <div className="fw-bold small mb-0" style={{ color: "#065f46" }}>
              Submitted to studio (final)
            </div>
            <div className="text-muted small">
              Use these for quotes and fulfilment — sent under the visitor&apos;s registered name.
            </div>
          </div>

          {submittedClientRows.length > 0 ? (
            <div className="px-3 py-3 border-bottom bg-white">
              <div className="fw-semibold small text-secondary mb-2">
                By client (click name for full details)
              </div>
              <div className="d-flex flex-column gap-2">
                {submittedClientRows.map((row) => {
                  const open = !!expandedSubmissionKeys[row.key];
                  const lines = row.kind === "cart" ? row.cartItems : row.wishlistItems;
                  return (
                    <div
                      key={row.key}
                      className="border rounded overflow-hidden"
                      style={{ borderColor: "#e5e7eb" }}
                    >
                      <div
                        role="button"
                        tabIndex={0}
                        className="d-flex align-items-center flex-wrap gap-2 px-3 py-2"
                        style={{
                          backgroundColor: "#fafaf8",
                          cursor: "pointer",
                        }}
                        onClick={() => toggleSubmissionExpanded(row.key)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleSubmissionExpanded(row.key);
                          }
                        }}
                      >
                        <span
                          className="text-muted user-select-none"
                          style={{ fontSize: "0.7rem", width: "1rem" }}
                          aria-hidden
                        >
                          {open ? "▼" : "▶"}
                        </span>
                        <button
                          type="button"
                          className="btn btn-link p-0 fw-semibold text-start text-decoration-none"
                          style={{ color: "#0071E3" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSubmissionDetail(row);
                          }}
                        >
                          {row.clientName}
                        </button>
                        <span className="small text-muted">
                          {row.clientContact || "—"}
                        </span>
                        <Badge
                          color={row.kind === "cart" ? "success" : "warning"}
                          pill
                          className="fw-semibold"
                        >
                          {row.kind === "cart" ? "Cart" : "Shortlist"}
                        </Badge>
                        <span className="small text-muted ms-auto">{row.summaryLine}</span>
                        {row.submittedAt ? (
                          <span className="small text-muted">
                            {row.submittedAt.toLocaleString("en-IN")}
                          </span>
                        ) : null}
                      </div>
                      {open ? (
                        <div className="px-3 py-3 bg-white border-top">
                          {lines.length === 0 ? (
                            <div className="text-muted small">No line items.</div>
                          ) : (
                            <div className="row g-2">
                              {lines.map((line: any, idx: number) => {
                                const img = String(line?.productImage || "").trim();
                                const isCart = row.kind === "cart";
                                const qty = isCart
                                  ? Math.max(1, Math.trunc(Number(line?.quantity) || 1))
                                  : 1;
                                const lineKey = String(
                                  line?.productUniqueId ||
                                    line?.productId ||
                                    line?.productKey ||
                                    idx
                                );
                                return (
                                  <div key={lineKey} className="col-12 col-md-6">
                                    <div className="d-flex gap-2 border rounded p-2 h-100 align-items-start">
                                      <div
                                        className="flex-shrink-0 rounded overflow-hidden bg-light border"
                                        style={{ width: 56, height: 56 }}
                                      >
                                        {img ? (
                                          <img
                                            src={img}
                                            alt=""
                                            style={{
                                              width: "100%",
                                              height: "100%",
                                              objectFit: "cover",
                                            }}
                                          />
                                        ) : null}
                                      </div>
                                      <div className="min-w-0 flex-grow-1">
                                        <div className="fw-semibold small text-truncate">
                                          {line?.productName || "—"}
                                        </div>
                                        <div className="small text-muted text-truncate">
                                          {line?.productUniqueId || line?.productId || ""}
                                        </div>
                                        {isCart ? (
                                          <div className="small mt-1">
                                            <span className="text-muted">Unit: </span>
                                            {formatMrpSnapshot(line?.sellPriceMrpSnapshot)}
                                            <span className="text-muted ms-2">Qty: </span>
                                            {qty}
                                          </div>
                                        ) : (
                                          <div className="small text-muted mt-1">
                                            Shortlisted (no price on shortlist)
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          {submittedInterestRows.length === 0 && submittedClientRows.length === 0 ? (
            <div className="p-3 text-muted small">
              No finalized shortlist or cart submissions yet for this catalogue.
            </div>
          ) : null}

          {submittedInterestRows.length > 0 ? (
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead style={{ backgroundColor: "#fafaf8" }}>
                  <tr className="small text-muted">
                    <th>Product</th>
                    <th>SKU / ID</th>
                    <th>Shortlisted</th>
                    <th>Cart intent</th>
                    <th>Last submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {submittedInterestRows.map((row) => (
                    <tr key={`sub-${row.productKey}`}>
                      <td className="fw-semibold">{row.productName || "—"}</td>
                      <td>
                        <code className="small">{row.productKey}</code>
                      </td>
                      <td>
                        {row.shortlistSubmissions > 0 ? (
                          <Badge
                            pill
                            style={{
                              backgroundColor: "#fff7ed",
                              color: "#9a3412",
                              border: "1px solid #fdba74",
                            }}
                          >
                            ×{row.shortlistSubmissions}
                          </Badge>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        {row.cartSubmissions > 0 ? (
                          <Badge
                            pill
                            style={{
                              backgroundColor: "#ecfdf5",
                              color: "#065f46",
                              border: "1px solid #6ee7b7",
                            }}
                          >
                            ×{row.cartSubmissions}
                            {row.cartUnits > row.cartSubmissions
                              ? ` · ${row.cartUnits} pcs`
                              : ""}
                          </Badge>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="small text-muted">
                        {row.lastActivityIso
                          ? new Date(row.lastActivityIso).toLocaleString("en-IN")
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : null}
        </CardBody>
      </Card>

      <Modal isOpen={!!submissionDetail} toggle={() => setSubmissionDetail(null)} size="lg">
        <ModalHeader toggle={() => setSubmissionDetail(null)}>Client submission details</ModalHeader>
        <ModalBody>
          {submissionDetail ? (
            <div className="small">
              <dl className="row mb-0">
                <dt className="col-sm-3 text-muted">Name</dt>
                <dd className="col-sm-9">{submissionDetail.clientName}</dd>
                <dt className="col-sm-3 text-muted">Phone</dt>
                <dd className="col-sm-9">{submissionDetail.clientContact || "—"}</dd>
                <dt className="col-sm-3 text-muted">Email</dt>
                <dd className="col-sm-9">{submissionDetail.clientEmail || "—"}</dd>
                <dt className="col-sm-3 text-muted">Type</dt>
                <dd className="col-sm-9 text-capitalize">{submissionDetail.kind}</dd>
                <dt className="col-sm-3 text-muted">Submitted</dt>
                <dd className="col-sm-9">
                  {submissionDetail.submittedAt
                    ? submissionDetail.submittedAt.toLocaleString("en-IN")
                    : "—"}
                </dd>
                <dt className="col-sm-3 text-muted">Source</dt>
                <dd className="col-sm-9">{submissionDetail.source || "—"}</dd>
                <dt className="col-sm-3 text-muted">Visitor id</dt>
                <dd className="col-sm-9">
                  <code className="small">{submissionDetail.catalogueVisitorId || "—"}</code>
                </dd>
                <dt className="col-sm-3 text-muted">Submission id</dt>
                <dd className="col-sm-9">
                  <code className="small">{submissionDetail.id}</code>
                </dd>
                <dt className="col-sm-3 text-muted">Message</dt>
                <dd className="col-sm-9" style={{ whiteSpace: "pre-wrap" }}>
                  {submissionDetail.clientMessage || "—"}
                </dd>
              </dl>
            </div>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setSubmissionDetail(null)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <Row>
        {/* Inquiries List */}
        <Col lg={4} className="mb-4">
          <Card className="h-100">
            <CardHeader 
              style={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #dee2e6" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="catalogue-panel-title">Inquiries ({filteredInquiries.length})</h6>
              </div>
            </CardHeader>
            <CardBody className="p-0">
              {/* Filters */}
              <div className="p-3 border-bottom">
                <Row>
                  <Col xs={6}>
                    <select 
                      className="form-select form-select-sm"
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                    >
                      <option value="all">All Status</option>
                      <option value="new">New</option>
                      <option value="replied">Replied</option>
                      <option value="closed">Closed</option>
                    </select>
                  </Col>
                  <Col xs={6}>
                    <select 
                      className="form-select form-select-sm"
                      value={filterPriority}
                      onChange={(e) => setFilterPriority(e.target.value)}
                    >
                      <option value="all">All Priority</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </Col>
                </Row>
              </div>

              {/* Inquiries List */}
              <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                {loading && <div className="p-3 text-muted">Loading inquiries...</div>}
                {filteredInquiries.map((inquiry) => (
                  <div
                    key={inquiry.id}
                    className={`p-3 border-bottom cursor-pointer ${
                      selectedInquiry?.id === inquiry.id ? "bg-light" : ""
                    }`}
                    onClick={() => setSelectedInquiry(inquiry)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="fw-bold small">{inquiry.customerName}</div>
                      <div className="d-flex gap-1">
                        {getPriorityBadge(inquiry.priority)}
                        {getStatusBadge(inquiry.status)}
                      </div>
                    </div>
                    <div className="small text-muted mb-2">{inquiry.productName}</div>
                    <div className="small mb-2" style={{ 
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden"
                    }}>
                      {inquiry.message}
                    </div>
                    <div className="small text-muted">{inquiry.timestamp}</div>
                  </div>
                ))}
              </div>

              {!loading && filteredInquiries.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-muted mb-0">No inquiries found</p>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>

        {/* Inquiry Detail */}
        <Col lg={8}>
          <Card>
            <CardHeader 
              style={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #dee2e6" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="catalogue-panel-title">Conversation · {selectedInquiry?.customerName || "Select inquiry"}</h6>
                <div className="d-flex gap-2">
                  {selectedInquiry && getPriorityBadge(selectedInquiry.priority)}
                  {selectedInquiry && getStatusBadge(selectedInquiry.status)}
                </div>
              </div>
            </CardHeader>
            <CardBody>
              {!selectedInquiry && <div className="text-muted">Select an inquiry to view details.</div>}
              {selectedInquiry && (
                <>
              {/* Customer Info */}
              <div className="mb-4 p-3 bg-light rounded">
                <Row>
                  <Col md={6}>
                    <div className="small">
                      <strong>Customer:</strong> {selectedInquiry.customerName}<br />
                      <strong>Phone:</strong> {selectedInquiry.phone || "Not provided"}<br />
                      <strong>Email:</strong> {selectedInquiry.email || "Not provided"}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="small">
                      <strong>Inquiry ID:</strong> {selectedInquiry.id}<br />
                      <strong>Time:</strong> {selectedInquiry.timestamp}<br />
                      <strong>Product:</strong> {selectedInquiry.productName}
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Original Message */}
              <div className="mb-4">
                <div className="fw-bold mb-2">Original message</div>
                <div className="p-3 border rounded bg-white">
                  {selectedInquiry.message}
                </div>
              </div>

              {/* Product Information (if applicable) */}
              {selectedInquiry.productId && (
                <div className="mb-4 p-3 bg-light rounded d-flex justify-content-between align-items-center">
                  <div>
                    <div className="fw-bold">{selectedInquiry.productName}</div>
                    <div className="small text-muted">
                      SKU: {selectedInquiry.productId} • Stock: Available • Price: On Request
                    </div>
                  </div>
                  <Button 
                    size="sm"
                    outline
                    style={{ 
                      borderColor: "#0071E3", 
                      color: "#0071E3"
                    }}
                  >
                    📱 View Product
                  </Button>
                </div>
              )}

              {/* Reply Section */}
              <div className="mb-4">
                <div className="fw-bold mb-2">Send reply</div>
                <textarea
                  className="form-control mb-3"
                  rows={4}
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="Type your reply here... (price, lead time, MOQ, etc.)"
                />
                
                {/* Action Buttons */}
                <div className="d-flex gap-2 flex-wrap">
                  <Button
                    size="sm"
                    style={{ 
                      backgroundColor: "#0071E3", 
                      borderColor: "#0071E3"
                    }}
                    onClick={handleSendReply}
                    disabled={sending}
                  >
                    📧 Send Internal Reply
                  </Button>
                  
                  {selectedInquiry.email && (
                    <Button
                      size="sm"
                      color="primary"
                      href={createEmailLink(selectedInquiry)}
                      target="_blank"
                    >
                      📧 Send Email
                    </Button>
                  )}
                  
                  {selectedInquiry.phone && (
                    <Button
                      size="sm"
                      color="success"
                      href={createWhatsAppLink(selectedInquiry)}
                      target="_blank"
                    >
                      📱 WhatsApp
                    </Button>
                  )}
                  
                  <Button
                    size="sm"
                    outline
                    color="secondary"
                    onClick={handleMarkAsClosed}
                    disabled={sending}
                  >
                    ✅ Mark as Closed
                  </Button>
                </div>
              </div>

              {/* Quick Reply Templates */}
              <div>
                <div className="fw-bold mb-2">⚡ Quick Reply Templates:</div>
                <div className="d-flex gap-2 flex-wrap">
                  <Button
                    size="sm"
                    outline
                    style={{ fontSize: "11px" }}
                    onClick={() => setReplyMessage("Thank you for your inquiry. We'll get back to you within 24 hours with detailed pricing and availability.")}
                  >
                    Standard Response
                  </Button>
                  <Button
                    size="sm"
                    outline
                    style={{ fontSize: "11px" }}
                    onClick={() => setReplyMessage("For bulk orders, we offer special pricing. Please share your required quantity and we'll provide you with the best rates.")}
                  >
                    Bulk Pricing
                  </Button>
                  <Button
                    size="sm"
                    outline
                    style={{ fontSize: "11px" }}
                    onClick={() => setReplyMessage("Yes, customization is available for this product. Please share your specific requirements and we'll provide you with options and pricing.")}
                  >
                    Customization
                  </Button>
                </div>
              </div>

              {!selectedInquiry.phone && (
                <div className="mt-3">
                  <small className="text-warning">
                    ⚠️ No phone number on record for this customer. Add via Share → Add New Customer to enable WhatsApp one-tap replies.
                  </small>
                </div>
              )}
              </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default InquiriesTab;
